import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { DWI_SECTION } from "./sections.js";

/**
 * "Open. Reply. Join." — the guest's three steps.
 *
 * Same markup and classes as the home `Journey` section and the photo-sharing
 * steps (`list-wrap` / `list-row` / `no-div` / `row-heading-div` /
 * `row-body-div serv`), so numbered steps look the same everywhere on the site.
 *
 * Three steps, and the order is the real one. Two things the copy must never
 * drift from, both checked in `digitalWeddingInvitation.seo.test.mjs`:
 *
 *  - Replying happens INSIDE the invitation. RSVP is a modal on the invitation
 *    page itself (invite/src/pages/RSVP.jsx), not a separate destination.
 *  - "Join" is a doorway, not a transfer. The invitation links to the join page
 *    (`/j/:eventId`), where the guest enters a name and a separate attendee
 *    record is created. Nothing carries over from their RSVP — the guest list
 *    and the gallery are two unlinked identity systems — so this step must
 *    describe an invitation that OPENS THE WAY to the wedding experience, never
 *    one that puts the guest inside it.
 */
const STEP_KEYS = ["open", "reply", "join"];

const InvitationSteps = ({ id }) => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();

  // One impression the first time the section scrolls into view; the observer
  // disconnects itself afterwards.
  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(DWI_SECTION.STEPS, analyticsContext),
  );

  return (
    <section ref={sectionRef} id={id} className="section wps-section">
      <div className="container home-serv">
        <div className="full-header-div">
          <div className="pad-20">
            <h2 className="heading-136">
              {t("digitalWeddingInvitation.steps.heading")}
            </h2>
          </div>
        </div>
        <ol className="list-wrap wps-steps">
          {STEP_KEYS.map((key, index) => (
            <li key={key} className="list-row">
              <div className="no-div">
                <div className="no">
                  {t("digitalWeddingInvitation.steps.stepLabel", {
                    number: index + 1,
                  })}
                </div>
              </div>
              <div className="row-heading-div">
                <h3 className="heading-38">
                  {t(`digitalWeddingInvitation.steps.items.${key}.title`)}
                </h3>
              </div>
              <div className="row-body-div serv">
                <p className="body-15">
                  {t(`digitalWeddingInvitation.steps.items.${key}.body`)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default InvitationSteps;
